import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InviteSubAccountForm } from "@/components/account/InviteSubAccountForm";
import { SponsoredPersonRow } from "@/components/account/SponsoredPersonRow";
import { RemoveSponsorshipDialog } from "@/components/account/RemoveSponsorshipDialog";
import { SponsorshipAuditLogTable } from "@/components/account/SponsorshipAuditLogTable";
import { GraduateCTA } from "@/components/account/GraduateCTA";
import { useSponsorshipStore } from "@/stores/sponsorship/sponsorship-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import type { MasterSponsorship } from "@/types/sponsorship";
import {
  getSponsorshipCap,
  getSponsorshipEligibility,
} from "@/utils/sponsorship";

type PeopleTab = "master" | "received" | "audit";

interface PeopleManagerProps {
  asPage?: boolean;
  headingLevel?: 1 | 2;
}

function formatPlanName(planType: string): string {
  return planType.charAt(0).toUpperCase() + planType.slice(1);
}

function formatPlanStatus(status: string): string {
  return status.replace("_", " ");
}

export const PeopleManager: React.FC<PeopleManagerProps> = ({
  asPage = false,
  headingLevel = 2,
}) => {
  const navigate = useNavigate();
  const Container = asPage ? "main" : "div";
  const {
    fetchAll,
    peopleAsMaster,
    pendingAsSub,
    activeAsSub,
    removeSponsorship,
    leaveSponsorship,
    acceptBundle,
    declineBundle,
    resendPendingInvite,
    cancelPendingInvite,
    error,
  } = useSponsorshipStore();
  const organizations = useOrganizationStore((state) => state.organizations);
  const fetchOrganizations = useOrganizationStore((state) => state.fetchOrganizations);

  const [tab, setTab] = useState<PeopleTab>("master");
  const [masterBillingOrgId, setMasterBillingOrgId] = useState("");
  const [selected, setSelected] = useState<MasterSponsorship | null>(null);
  const [inviteActionMessage, setInviteActionMessage] = useState<string | null>(null);

  useEffect(() => {
    void fetchAll();
    void fetchOrganizations();
  }, [fetchAll, fetchOrganizations]);

  const adminOrganizations = organizations.filter(
    (organization) =>
      organization.membership?.role === "owner" ||
      organization.membership?.role === "admin",
  );
  const eligibleOrganizations = adminOrganizations.filter((organization) =>
    getSponsorshipEligibility(organization).eligible,
  );
  const selectedOrganization = eligibleOrganizations.find(
    (organization) => organization._id === masterBillingOrgId,
  );
  const selectedEligibility = selectedOrganization
    ? getSponsorshipEligibility(selectedOrganization)
    : null;

  useEffect(() => {
    if (
      masterBillingOrgId &&
      eligibleOrganizations.some(
        (organization) => organization._id === masterBillingOrgId,
      )
    ) {
      return;
    }

    if (eligibleOrganizations.length === 1) {
      setMasterBillingOrgId(eligibleOrganizations[0]._id);
      return;
    }

    if (masterBillingOrgId) {
      setMasterBillingOrgId("");
    }
  }, [eligibleOrganizations, masterBillingOrgId]);

  useEffect(() => {
    setInviteActionMessage(null);
  }, [masterBillingOrgId]);

  const filteredPeopleAsMaster = masterBillingOrgId
    ? peopleAsMaster.filter(
        (sponsorship) => sponsorship.masterBillingOrgId === masterBillingOrgId,
      )
    : [];

  const now = Date.now();
  const activeCount = filteredPeopleAsMaster.filter(
    (sponsorship) => sponsorship.status === "active",
  ).length;
  const pendingCount = filteredPeopleAsMaster.filter((sponsorship) => {
    if (sponsorship.status !== "pending" || !sponsorship.pendingExpiresAt) {
      return false;
    }
    return new Date(sponsorship.pendingExpiresAt).getTime() > now;
  }).length;
  const selectedPlanCap = selectedOrganization
    ? getSponsorshipCap(selectedOrganization.billingPlan.type)
    : 0;
  const usedSlots = activeCount + pendingCount;

  const handleConfirmRemove = async () => {
    if (!selected) {
      return;
    }

    await removeSponsorship(selected._id);
    setSelected(null);
  };

  const handleInviteSent = (message: string) => {
    setInviteActionMessage(message);
  };

  const handleResendPending = async (sponsorship: MasterSponsorship) => {
    if (!sponsorship.pendingBundleId) {
      return;
    }

    try {
      await resendPendingInvite(sponsorship.pendingBundleId);
      setInviteActionMessage(
        `Invite resent to ${sponsorship.invitedEmail}. Only the newest email link should be used.`,
      );
    } catch {
      setInviteActionMessage(null);
    }
  };

  const handleCancelPending = async (sponsorship: MasterSponsorship) => {
    if (!sponsorship.pendingBundleId) {
      return;
    }

    try {
      await cancelPendingInvite(sponsorship.pendingBundleId);
      setInviteActionMessage(
        `Pending invite canceled for ${sponsorship.invitedEmail}.`,
      );
    } catch {
      setInviteActionMessage(null);
    }
  };

  const Heading = headingLevel === 1 ? "h1" : "h2";

  return (
    <Container className={asPage ? "mx-auto max-w-5xl space-y-6 p-6" : "space-y-6"}>
      <div>
        <Heading className={headingLevel === 1 ? "text-2xl font-semibold" : "text-xl font-semibold"}>
          People
        </Heading>
        <p className="text-sm text-muted-foreground">
          Manage AI-budget sponsorships separately from workspace access. Sponsorship
          only covers AI budget, and access grants stay explicit.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant={tab === "master" ? "default" : "outline"} onClick={() => setTab("master")}>
          People I sponsor
        </Button>
        <Button variant={tab === "received" ? "default" : "outline"} onClick={() => setTab("received")}>
          Sponsorships received
        </Button>
        <Button variant={tab === "audit" ? "default" : "outline"} onClick={() => setTab("audit")}>
          Audit log
        </Button>
      </div>

      {tab === "master" ? (
        <section className="space-y-5">
          <div className="rounded-md border p-4">
            <div className="mb-4 grid gap-2">
              <Label htmlFor="master-billing-org">Billing organization</Label>
              <Select value={masterBillingOrgId} onValueChange={setMasterBillingOrgId}>
                <SelectTrigger id="master-billing-org">
                  <SelectValue
                    placeholder={
                      eligibleOrganizations.length === 0
                        ? "No eligible organizations"
                        : "Select a paid organization"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {adminOrganizations.map((organization) => {
                    const eligibility = getSponsorshipEligibility(organization);

                    return (
                      <SelectItem
                        key={organization._id}
                        value={organization._id}
                        disabled={!eligibility.eligible}
                      >
                        {organization.name}
                        {eligibility.reason
                          ? ` (${eligibility.reason})`
                          : ` (${formatPlanName(organization.billingPlan.type)})`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {selectedOrganization ? (
                <p className="text-xs text-muted-foreground">
                  {formatPlanName(selectedOrganization.billingPlan.type)} plan,{" "}
                  {formatPlanStatus(selectedOrganization.billingPlan.status)} status
                  {selectedEligibility?.reason ? `, ${selectedEligibility.reason.toLowerCase()}` : ""}.
                </p>
              ) : eligibleOrganizations.length === 0 ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground">
                    Sponsorship requires an owner/admin organization with an active paid plan.
                  </p>
                  {adminOrganizations.length > 0 ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/billing")}
                    >
                      Manage billing
                    </Button>
                  ) : null}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Choose the paid organization whose AI budget should sponsor this user.
                </p>
              )}
            </div>
            {masterBillingOrgId && selectedEligibility?.eligible ? (
              <InviteSubAccountForm
                masterBillingOrgId={masterBillingOrgId}
                onInviteSent={handleInviteSent}
              />
            ) : null}
          </div>

          <div className="rounded-md border p-4">
            {selectedOrganization ? (
              <div className="mb-2 text-sm text-muted-foreground">
                {selectedPlanCap >= 999999
                  ? `${usedSlots} sponsor slots in use (${activeCount} active, ${pendingCount} pending)`
                  : `Used ${usedSlots} of ${selectedPlanCap} sponsor slots (${activeCount} active, ${pendingCount} pending)`}
              </div>
            ) : null}
            {inviteActionMessage ? (
              <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                {inviteActionMessage}
              </div>
            ) : null}
            {error ? (
              <p className="mb-3 text-sm text-destructive">{error}</p>
            ) : null}
            {filteredPeopleAsMaster.map((sponsorship) => (
              <SponsoredPersonRow
                key={sponsorship._id}
                sponsorship={sponsorship}
                onRemove={setSelected}
                onResendPending={(pendingSponsorship) =>
                  void handleResendPending(pendingSponsorship)
                }
                onCancelPending={(pendingSponsorship) =>
                  void handleCancelPending(pendingSponsorship)
                }
              />
            ))}
            {selectedOrganization && filteredPeopleAsMaster.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No sponsored people for {selectedOrganization.name} yet.
              </p>
            ) : null}
            {!selectedOrganization && eligibleOrganizations.length > 1 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Select an organization to view its sponsored people and invite status.
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      {tab === "received" ? (
        <section className="rounded-md border p-4">
          {error ? <p className="mb-3 text-sm text-destructive">{error}</p> : null}
          <h2 className="mb-3 text-lg font-medium">Pending invites</h2>
          {pendingAsSub.map((bundle) => (
            <div key={bundle._id} className="flex items-center justify-between border-b py-3 last:border-b-0">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{bundle.invitedEmail}</p>
                <p className="text-xs text-muted-foreground">
                  AI budget only. Any workspace access is granted separately.
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => void acceptBundle(bundle._id)}>
                  Accept
                </Button>
                <Button size="sm" variant="outline" onClick={() => void declineBundle(bundle._id)}>
                  Decline
                </Button>
              </div>
            </div>
          ))}
          {pendingAsSub.length === 0 ? (
            <p className="pb-2 text-sm text-muted-foreground">No pending invites.</p>
          ) : null}
          <h2 className="mb-3 mt-6 text-lg font-medium">Active sponsorships</h2>
          {activeAsSub.map((sponsorship) => (
            <div key={sponsorship._id} className="flex items-center justify-between border-b py-3 last:border-b-0">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{sponsorship.invitedEmail}</p>
                <p className="text-xs text-muted-foreground">
                  AI budget only. Workspace access continues only where it was explicitly granted.
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => void leaveSponsorship(sponsorship._id)}>
                End sponsorship
              </Button>
            </div>
          ))}
          {activeAsSub.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active sponsorships.</p>
          ) : null}
          <div className="mt-4 flex justify-end">
            <GraduateCTA />
          </div>
        </section>
      ) : null}

      {tab === "audit" ? <SponsorshipAuditLogTable /> : null}

      <RemoveSponsorshipDialog
        sponsorship={selected}
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelected(null);
          }
        }}
        onConfirm={handleConfirmRemove}
      />
    </Container>
  );
};
